import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { axiosPost } from "@/services/axiosService";
import { useSelector } from "react-redux";
import { State } from "@/redux/Types";

interface Props {
  onClose: () => void;
  Question?: any;
  id: any;
  Answer?: any;
  type: any;
  onSave?: (updatedAnswer) => void;
  preserved?: boolean;
  hideQuestionLabel?: boolean;
}

function AddQuestionModal({
  onClose,
  id,
  Answer,
  Question,
  type,
  onSave,
  preserved = false,
  hideQuestionLabel = false,
}: Props) {
  const fromOutgrow = useSelector((state: State) => state.user.fromOutgrow);
  const [answer, setAnswer] = useState(Answer);
  const [question, setQuestion] = useState(type === "add" ? "" : Question);
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const addQuestion = async () => {
    try {
      if (type == "add") {
        if (!question || !answer || isLoading) return;
        setIsLoading(true);
        await axiosPost(`/analysis/addQuestion`, {
          answer,
          question,
          botCustomId: id,
          preserved: true,
        });
      } else {
        if (!answer || isLoading || answer === Answer) return;
        setIsLoading(true);
        await axiosPost(`/analysis/update`, {
          answer,
          question,
          cacheResponseId: id,
          preserved: true,
          fromPreservationMode: true,
        });
      }

      queryClient.invalidateQueries({
        queryKey: ["allQuestionsData"],
      });
      onClose();

      onSave && onSave(answer);
    } catch (error) {
      console.error("Error adding question:", error);
    }
    setIsLoading(false);
  };

  return (
    <div className="createbot-modal createbot-new-background" onClick={onClose}>
      <div
        className="integration-modal crisp-integration-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="integration-content crisp-integration-content !bg-gradient-to-b from-[#D1D1D1] from-0% via-white via-[15.51%] to-white">
          <div className="relative flex flex-col justify-center gap-[8px] border-b border-b-[#0000001A] p-[20px]">
            <h2 className="text-center text-[16px] font-medium text-[#444]">
              {type === "add"
                ? "Add Question and answer for Agent"
                : "Update Answer"}
            </h2>
            <p className="text-center text-[14px] text-[#666666]">
              Update the right answer for user
            </p>
            <div className="closing-modal">
              <button onClick={onClose}>
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 1.00714L8.99286 0L5 3.99286L1.00714 0L0 1.00714L3.99286 5L0 8.99286L1.00714 10L5 6.00714L8.99286 10L10 8.99286L6.00714 5L10 1.00714Z"
                    fill="#6D6D6D"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
          <div className="rounded-b-[10px] p-[30px]">
            <div className="grid gap-[8px]">
              <span className="text-[12px] font-medium uppercase text-[rgba(102,102,102,0.6)]">
                User's Question
              </span>
              {type != "add" && (
                <p className="line-clamp-2 break-all text-[16px] font-medium text-text-clr">
                  {question}
                </p>
              )}
              <div className="text-[16px] font-medium text-text-clr">
                {type === "add" ? (
                  <textarea
                    className="border-[rgba(0, 0, 0, 0.20)] h-[64px] overflow-auto rounded-[10px] border bg-[#fafafa] p-[10px] text-[14px] placeholder:text-[15px]"
                    placeholder="Type a Question here..."
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                  />
                ) : (
                  <></>
                )}
              </div>
            </div>

            <div className="mt-[20px] grid gap-[8px]">
              <span className="flex items-center gap-[5px] text-[12px] font-medium uppercase text-[rgba(102,102,102,0.6)]">
                {" Agent Answer"}
              </span>
              <textarea
                name=""
                id=""
                className="border-[rgba(0, 0, 0, 0.20)] h-[227px] overflow-auto rounded-[10px] border bg-white p-[10px] text-[14px] placeholder:text-[15px]"
                placeholder="Type your answer here..."
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
              ></textarea>
            </div>
            <div className="mt-[20px] flex items-center justify-center gap-[10px]">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="rounded-[8px] border border-primary-clr px-[20px] py-[10px] text-[14px] font-medium text-primary-clr"
              >
                Cancel
              </button>
              <button
                className={`rounded-[8px] bg-primary-clr px-[20px] py-[10px] text-[14px] font-medium text-white ${Answer === answer && "pointer-events-none opacity-50"}`}
                disabled={isLoading}
                onClick={addQuestion}
              >
                {isLoading
                  ? type === "add"
                    ? "Adding..."
                    : "Updating..."
                  : type === "add"
                    ? "Add Question"
                    : "Update Answer"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddQuestionModal;
